import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Region {
  name: string;
  lat: number;
  lng: number;
  services: {
    baas: 'operational' | 'degraded' | 'unavailable';
    draas: 'operational' | 'degraded' | 'unavailable';
    airGap?: 'operational' | 'degraded' | 'unavailable';
  };
}

const regions: Region[] = [
  { 
    name: 'China Mainland', 
    lat: 35.8617, 
    lng: 104.1954,
    services: {
      baas: 'operational',
      draas: 'operational',
      airGap: 'operational'
    }
  },
  { 
    name: 'Hong Kong', 
    lat: 22.3193, 
    lng: 114.1694,
    services: {
      baas: 'operational',
      draas: 'operational'
    }
  },
  { 
    name: 'Singapore', 
    lat: 1.3521, 
    lng: 103.8198,
    services: {
      baas: 'operational',
      draas: 'operational',
      airGap: 'operational'
    }
  },
  { 
    name: 'Malaysia', 
    lat: 4.2105, 
    lng: 101.9758,
    services: {
      baas: 'operational',
      draas: 'operational'
    }
  },
  { 
    name: 'Indonesia', 
    lat: -0.7893, 
    lng: 113.9213,
    services: {
      baas: 'operational',
      draas: 'operational'
    }
  },
  { 
    name: 'United States', 
    lat: 37.0902, 
    lng: -95.7129,
    services: {
      baas: 'operational',
      draas: 'operational',
      airGap: 'operational'
    }
  },
  { 
    name: 'Europe', 
    lat: 50.1109, 
    lng: 8.6821,
    services: {
      baas: 'operational',
      draas: 'degraded',
      airGap: 'operational'
    }
  },
  { 
    name: 'Middle East', 
    lat: 25.2048, 
    lng: 55.2708,
    services: {
      baas: 'operational',
      draas: 'unavailable'
    }
  },
];

const getStatusColor = (status: 'operational' | 'degraded' | 'unavailable'): string => {
  switch (status) {
    case 'operational':
      return '#10b981'; // Green
    case 'degraded':
      return '#fbbf24'; // Amber
    case 'unavailable':
      return '#ef4444'; // Red
  }
};

const getServiceIcon = (service: 'baas' | 'draas' | 'airGap'): string => {
  switch (service) {
    case 'baas':
      return 'ri-database-2-line'; // Database/Storage icon
    case 'draas':
      return 'ri-refresh-line'; // Sync/Recovery icon
    case 'airGap':
      return 'ri-shield-check-line'; // Shield/Lock icon
  }
};

const LeafletWorldMap = () => {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Initialize map with dark blue theme (Azure-style)
    const map = L.map(containerRef.current, {
      center: [20, 30],
      zoom: 2,
      minZoom: 2,
      maxZoom: 6,
      zoomControl: true,
      attributionControl: false,
    });

    mapRef.current = map;

    // Use CartoDB Dark Matter tiles for clean, professional look
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', {
      attribution: '',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    // Add region markers (dots only, no connections)
    regions.forEach((region) => {
      // Single solid dot - minimal style
      const dotRadius = 8;

      // Main dot
      const dot = L.circleMarker([region.lat, region.lng], {
        radius: dotRadius,
        fillColor: '#3b82f6', // Blue dot
        color: '#60a5fa', // Light blue border
        weight: 2,
        opacity: 0.9,
        fillOpacity: 0.7,
      }).addTo(map);

      // Optional subtle halo
      L.circleMarker([region.lat, region.lng], {
        radius: dotRadius + 4,
        fillColor: '#3b82f6',
        color: '#3b82f6',
        weight: 0,
        opacity: 0.2,
        fillOpacity: 0.1,
      }).addTo(map);

      // Build service icons HTML (icon-only, color-coded)
      const serviceIcons = Object.entries(region.services)
        .map(([serviceKey, status]) => {
          const service = serviceKey as 'baas' | 'draas' | 'airGap';
          const statusColor = getStatusColor(status);
          const iconClass = getServiceIcon(service);
          
          return `
            <i class="${iconClass}" style="
              color: ${statusColor};
              font-size: 12px;
              margin: 0 3px;
            "></i>
          `;
        })
        .join('');

      // Create permanent label with region name and service icons
      const label = L.tooltip({
        permanent: true,
        direction: 'right',
        offset: [dotRadius + 8, 0],
        className: 'region-label-tooltip',
      }).setContent(`
        <div style="
          background: transparent;
          border: none;
          padding: 0;
          font-size: 11px;
          font-weight: 500;
          color: #cbd5e1;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 6px;
        ">
          <span style="color: #e2e8f0;">${region.name}</span>
          <span style="display: flex; align-items: center; gap: 2px;">
            ${serviceIcons}
          </span>
        </div>
      `);

      dot.bindTooltip(label);

      // Hover interaction - show detailed tooltip
      const hoverTooltip = L.tooltip({
        permanent: false,
        direction: 'top',
        offset: [0, -dotRadius - 5],
        className: 'region-hover-tooltip',
      });

      const buildHoverContent = () => {
        const serviceDetails = Object.entries(region.services)
          .map(([serviceKey, status]) => {
            const service = serviceKey as 'baas' | 'draas' | 'airGap';
            const statusColor = getStatusColor(status);
            const iconClass = getServiceIcon(service);
            const serviceName = service === 'baas' ? 'BaaS' : service === 'draas' ? 'DRaaS' : 'Air-Gapped Vault';
            const statusText = status.charAt(0).toUpperCase() + status.slice(1);
            
            return `
              <div style="
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 3px 0;
              ">
                <i class="${iconClass}" style="color: ${statusColor}; font-size: 11px;"></i>
                <span style="color: #cbd5e1; font-size: 10px;">${serviceName}</span>
                <span style="color: ${statusColor}; font-size: 10px; font-weight: 600;">${statusText}</span>
              </div>
            `;
          })
          .join('');

        return `
          <div style="
            background: rgba(15, 23, 42, 0.95);
            border: 1px solid #334155;
            border-radius: 4px;
            padding: 8px 10px;
            min-width: 160px;
          ">
            <div style="
              color: #e2e8f0;
              font-weight: 600;
              font-size: 11px;
              margin-bottom: 6px;
              border-bottom: 1px solid #334155;
              padding-bottom: 4px;
            ">${region.name}</div>
            ${serviceDetails}
          </div>
        `;
      };

      hoverTooltip.setContent(buildHoverContent());

      dot.on('mouseover', () => {
        dot.setStyle({
          fillOpacity: 1,
          opacity: 1,
          weight: 3,
        });
        dot.bindTooltip(hoverTooltip).openTooltip();
      });

      dot.on('mouseout', () => {
        dot.setStyle({
          fillOpacity: 0.7,
          opacity: 0.9,
          weight: 2,
        });
        dot.unbindTooltip(hoverTooltip);
        dot.bindTooltip(label);
      });
    });

    // Cleanup
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 1 }}
    />
  );
};

export default LeafletWorldMap;
